import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api, formatDate, formatEUR } from "../lib/api";
import { useT } from "../i18n";
import { Camera, FileText, ChevronRight, ChevronLeft, MessageCircle, CalendarDays, BellRing } from "lucide-react";

const STATUS_META = {
  pending: { key: "status.pending", cls: "chip-pending" },
  processed: { key: "status.processed", cls: "chip-processed" },
  error: { key: "status.error", cls: "chip-error" },
};

const NOW = new Date();

export default function ClientDashboard() {
  const { t } = useT();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthFilter, setMonthFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [eventsByYear, setEventsByYear] = useState({});
  const [vatRegime, setVatRegime] = useState("monthly");
  const [calYear, setCalYear] = useState(null);
  const [noCompany, setNoCompany] = useState(false);
  const [viewY, setViewY] = useState(NOW.getFullYear());
  const [viewM, setViewM] = useState(NOW.getMonth() + 1); // 1-12

  const loadYear = useCallback(async (year) => {
    try {
      const { data } = await api.get("/fiscal-calendar", { params: { year } });
      setVatRegime(data.vat_regime);
      setEventsByYear((prev) => ({ ...prev, [year]: data.events || [] }));
    } catch (e) {
      if (e.response?.status === 400) setNoCompany(true);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/invoices", { params: { mine: true } });
        setInvoices(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!noCompany && !eventsByYear[viewY]) loadYear(viewY);
  }, [viewY, eventsByYear, noCompany, loadYear]);

  useEffect(() => {
    if (eventsByYear[viewY] !== undefined) setCalYear(viewY);
  }, [eventsByYear, viewY]);

  const todayIso = NOW.toISOString().slice(0, 10);
  const limitIso = new Date(NOW.getTime() + 7 * 86400000).toISOString().slice(0, 10);
  const curYear = NOW.getFullYear();
  const reminders = useMemo(
    () => (eventsByYear[curYear] || []).filter((e) => e.date >= todayIso && e.date <= limitIso),
    [eventsByYear, curYear, todayIso, limitIso],
  );

  const viewYm = `${viewY}-${String(viewM).padStart(2, "0")}`;
  const monthEvents = useMemo(
    () => (eventsByYear[viewY] || []).filter((e) => e.date.slice(0, 7) === viewYm),
    [eventsByYear, viewY, viewYm],
  );

  const shiftMonth = (delta) => {
    let m = viewM + delta;
    let y = viewY;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setViewY(y);
    setViewM(m);
  };

  const viewMonthLabel = `${new Date(viewY, viewM - 1, 1).toLocaleDateString(undefined, { month: "long" })} ${viewY}`;
  const calendarReady = !noCompany && calYear !== null;

  const months = useMemo(() => {
    const set = new Set(invoices.map((i) => (i.created_at || "").slice(0, 7)).filter(Boolean));
    return [...set].sort().reverse();
  }, [invoices]);

  const filtered = useMemo(() => invoices.filter((i) => {
    const monthOk = !monthFilter || (i.created_at || "").slice(0, 7) === monthFilter;
    const statusOk = !statusFilter || i.status === statusFilter;
    return monthOk && statusOk;
  }), [invoices, monthFilter, statusFilter]);

  const monthLabel = (ym) => {
    const [y, m] = ym.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  };

  const stats = {
    total: filtered.length,
    pending: filtered.filter((i) => i.status === "pending").length,
    processed: filtered.filter((i) => i.status === "processed").length,
    sum: filtered.reduce((acc, i) => acc + (Number(i.extracted_fields?.total) || 0), 0),
  };

  return (
    <div className="paper-grain min-h-screen pb-32">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="client-dashboard">
        <span className="label-eyebrow">{t("cd.eyebrow")}</span>
        <div className="flex items-end justify-between flex-wrap gap-3">
          <h1 className="font-display text-3xl sm:text-4xl mt-2">{t("cd.title")}</h1>
          <div className="flex gap-2">
            <Link to="/cliente/mensagens" className="btn-secondary inline-flex items-center gap-2 text-sm" data-testid="client-messages-btn">
              <MessageCircle size={16} /> {t("common.messages")}
            </Link>
          </div>
        </div>

        {/* Fiscal reminders */}
        {reminders.length > 0 && (
          <button
            type="button"
            onClick={() => document.querySelector('[data-testid="embedded-fiscal-calendar"]')?.scrollIntoView({ behavior: "smooth" })}
            className="block w-full text-left mt-6 rounded-xl border border-[#FDE68A] bg-[#FEF9EC] p-5 hover:border-[#FCD34D] transition-colors"
            data-testid="fiscal-reminders-card"
          >
            <div className="flex items-center gap-2 text-[#92400E]">
              <BellRing size={18} />
              <span className="label-eyebrow text-[#92400E]">{t("cd.reminders")}</span>
            </div>
            <ul className="mt-3 space-y-1.5">
              {reminders.map((e) => (
                <li key={`${e.date}-${e.title}`} className="flex items-center gap-3 text-sm">
                  <span className="font-mono-data text-[#92400E] w-20 shrink-0">{formatDate(e.date)}</span>
                  <span className="text-[color:var(--ink)]">{e.title}</span>
                </li>
              ))}
            </ul>
          </button>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          {[
            { label: t("cd.stat_total"), value: stats.total, key: "total" },
            { label: t("status.pending"), value: stats.pending, key: "pending" },
            { label: t("status.processed_plural"), value: stats.processed, key: "processed" },
            { label: t("cd.sum"), value: formatEUR(stats.sum), key: "sum" },
          ].map((s) => (
            <div key={s.key} className="card-soft p-4 sm:p-5" data-testid={`stat-${s.key}`}>
              <div className="label-eyebrow">{s.label}</div>
              <div className="font-display text-2xl sm:text-3xl mt-1">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mt-4">
          <div>
            <label className="label-eyebrow block mb-1">{t("cd.filter_month")}</label>
            <select className="input-soft py-2 text-sm min-w-[160px]" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} data-testid="month-filter">
              <option value="">{t("cd.filter_all_months")}</option>
              {months.map((ym) => (
                <option key={ym} value={ym}>{monthLabel(ym)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">{t("cd.filter_status")}</label>
            <select className="input-soft py-2 text-sm min-w-[150px]" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter">
              <option value="">{t("cd.filter_all_status")}</option>
              <option value="pending">{t("status.pending")}</option>
              <option value="processed">{t("status.processed")}</option>
              <option value="error">{t("status.error")}</option>
            </select>
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="text-[color:var(--ink-muted)]">{t("common.loading")}</div>
          ) : filtered.length === 0 ? (
            <div className="card-soft text-center py-12" data-testid="empty-state">
              <FileText className="mx-auto text-[color:var(--ink-muted)]" size={28} />
              <p className="mt-4 font-display text-xl">{t("cd.empty_title")}</p>
              <p className="text-sm text-[color:var(--ink-muted)] mt-1">{t("cd.empty_desc")}</p>
              <Link to="/cliente/nova-fatura" className="btn-primary inline-flex items-center gap-2 mt-6" data-testid="empty-new-invoice-btn">
                <Camera size={16} /> {t("common.new_invoice")}
              </Link>
            </div>
          ) : (
            <ul className="space-y-3">
              {filtered.map((inv) => {
                const s = STATUS_META[inv.status] || STATUS_META.pending;
                const ef = inv.extracted_fields || {};
                return (
                  <li key={inv.id}>
                    <Link
                      to={`/cliente/fatura/${inv.id}`}
                      className="card-soft flex items-center gap-4 row-hover transition-colors"
                      data-testid={`invoice-row-${inv.id}`}
                    >
                      <div className="w-12 h-12 rounded-xl bg-[color:var(--paper)] border border-[color:var(--border)] flex items-center justify-center">
                        <FileText size={18} className="text-[color:var(--primary)]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{ef.vendor || t("common.vendor_unknown")}</span>
                          <span className={`chip ${s.cls}`}>{t(s.key)}</span>
                        </div>
                        <div className="text-sm text-[color:var(--ink-muted)] mt-1 flex gap-3 font-mono-data">
                          <span>{formatDate(inv.created_at)}</span>
                          <span>{formatEUR(ef.total)}</span>
                          {ef.nif && <span>NIF {ef.nif}</span>}
                          {inv.source === "email" && <span className="text-[color:var(--primary)]" data-testid={`source-email-${inv.id}`}>· {t("source.email")}</span>}
                        </div>
                      </div>
                      <ChevronRight size={18} className="text-[color:var(--ink-muted)]" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Embedded fiscal calendar */}
        {noCompany ? (
          <div className="mt-12 card-soft flex items-start gap-3" data-testid="no-company-message">
            <BellRing size={18} className="text-[color:var(--primary)] mt-0.5 shrink-0" />
            <p className="text-sm text-[color:var(--ink-muted)]">{t("cd.no_company")}</p>
          </div>
        ) : calendarReady && (
          <div className="mt-12" data-testid="embedded-fiscal-calendar">
            <div className="flex items-end justify-between flex-wrap gap-2">
              <div>
                <span className="label-eyebrow">{t("fc.eyebrow")}</span>
                <h2 className="font-display text-2xl sm:text-3xl mt-1 flex items-center gap-2">
                  <CalendarDays className="text-[color:var(--primary)]" size={24} /> {t("fc.title")}
                </h2>
              </div>
              <div className="text-sm text-[color:var(--ink-muted)]">
                {t("fc.regime")}: <span className="font-medium text-[color:var(--ink)]">{vatRegime === "quarterly" ? t("fc.quarterly") : t("fc.monthly")}</span>
              </div>
            </div>
            <p className="text-xs text-[color:var(--ink-muted)] flex items-start gap-2 mt-2">
              <BellRing size={14} className="mt-0.5 shrink-0" /> {t("fc.disclaimer")}
            </p>

            {/* Month navigator */}
            <div className="card-soft mt-5">
              <div className="flex items-center justify-between gap-3">
                <button onClick={() => shiftMonth(-1)} className="btn-secondary inline-flex items-center gap-1 text-sm" data-testid="cal-prev-month">
                  <ChevronLeft size={16} /> {t("fc.prev")}
                </button>
                <h3 className="font-display text-lg capitalize text-center flex-1" data-testid="cal-current-month">{viewMonthLabel}</h3>
                <button onClick={() => shiftMonth(1)} className="btn-secondary inline-flex items-center gap-1 text-sm" data-testid="cal-next-month">
                  {t("fc.next")} <ChevronRight size={16} />
                </button>
              </div>
              {monthEvents.length === 0 ? (
                <p className="text-sm text-[color:var(--ink-muted)] text-center py-6" data-testid="cal-no-events">{t("fc.no_events")}</p>
              ) : (
                <ul className="mt-4 divide-y divide-[color:var(--border)]">
                  {monthEvents.map((e) => (
                    <li key={`${e.date}-${e.title}`} className="flex items-start gap-4 py-3">
                      <div className="text-center w-10 shrink-0">
                        <div className="font-display text-xl leading-none">{e.date.slice(8, 10)}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">{e.title}</span>
                        <p className="text-sm text-[color:var(--ink-muted)] mt-0.5">{e.description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </main>

      <Link
        to="/cliente/nova-fatura"
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 rounded-full h-14 px-7 bg-[color:var(--primary)] text-white flex items-center gap-3 shadow-[0_15px_35px_-10px_rgba(26,58,50,0.5)] hover:-translate-y-0.5 hover:bg-[color:var(--primary-hover)] transition-all"
        data-testid="floating-new-invoice-btn"
      >
        <Camera size={20} />
        <span className="font-semibold">{t("common.new_invoice")}</span>
      </Link>
    </div>
  );
}
